// src/admin/customers/CustomersTable.jsx
import React, { useState, useEffect } from "react";

// ── Shared helpers ──────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:    { dot: "#059669", text: "#059669" },
  flagged:   { dot: "#DC2626", text: "#DC2626" },
  suspended: { dot: "#94A3B8", text: "#64748B" },
};

function relativeTime(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff}d ago`;
}

function avatarBg(name) {
  const palette = ["#EEF2FF", "#F0FDF4", "#FEF3C7", "#FEE2E2", "#E0F2FE", "#FCE7F3", "#F5F3FF"];
  return palette[name.charCodeAt(0) % palette.length];
}

function avatarColor(name) {
  const palette = ["#4338CA", "#16A34A", "#D97706", "#DC2626", "#0369A1", "#BE185D", "#7C3AED"];
  return palette[name.charCodeAt(0) % palette.length];
}

function initials(name) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("");
}

// ── Dropdown item ───────────────────────────────────────────────────────────

function DropdownItem({ label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        padding: "10px 16px",
        textAlign: "left",
        border: "none",
        background: "transparent",
        fontSize: 13,
        fontWeight: 500,
        color: danger ? "#DC2626" : "#334155",
        cursor: "pointer",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {label}
    </button>
  );
}

// ── Sort button ─────────────────────────────────────────────────────────────

function SortTh({ label, field, sortField, sortDir, onSort, align = "left", width }) {
  return (
    <th style={{ width, textAlign: align, padding: "0 12px" }}>
      <button
        onClick={() => onSort(field)}
        style={{
          background: "none",
          border: "none",
          fontSize: 11,
          fontWeight: 700,
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: align === "right" ? "flex-end" : "flex-start",
          gap: 4,
          width: "100%",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        {label}
        <span style={{ opacity: 0.6 }}>
          {sortField === field ? (sortDir === 1 ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

// ── Skeleton loading ────────────────────────────────────────────────────────

function SkeletonTable() {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #F1F5F9", overflow: "hidden" }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <thead>
          <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", height: 44 }}>
            {[44, 220, 160, 130, 80, 120, 110, 110, 140].map((w, i) => (
              <th key={i} style={{ width: w, padding: "0 12px" }} />
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(8)].map((_, i) => (
            <tr key={i} style={{ height: 56, borderBottom: "1px solid #F8FAFC" }}>
              {[...Array(9)].map((_, j) => (
                <td key={j} style={{ padding: "0 12px" }}>
                  <div
                    style={{
                      height: 13,
                      borderRadius: 4,
                      background:
                        "linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                      width: j === 0 ? 20 : j === 1 ? "75%" : "60%",
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function CustomersTable({
  customers,
  allCustomers,
  selectedIds,
  onRowClick,
  onSelect,
  onSelectAll,
  onSort,
  sortField,
  sortDir,
  currentPage,
  onPageChange,
  pageSize,
  totalPages,
  loading,
  onClearFilters,
  onFlagCustomer,
  onSuspendCustomer,
  onMessageCustomer,
}) {
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".cust-dropdown")) setOpenDropdownId(null);
    };
    const escape = (e) => { if (e.key === "Escape") setOpenDropdownId(null); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  if (loading) return <SkeletonTable />;

  const allSelectedOnPage =
    customers.length > 0 && customers.every((c) => selectedIds.has(c.id));
  const someSelectedOnPage = customers.some((c) => selectedIds.has(c.id));

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, allCustomers.length);

  if (customers.length === 0) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #F1F5F9",
          padding: 60,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#0F172A",
            marginBottom: 6,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          No customers found
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#94A3B8",
            marginBottom: 16,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          Try adjusting your filters or search term
        </div>
        <button
          onClick={onClearFilters}
          style={{
            padding: "8px 20px",
            background: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            color: "#64748B",
            cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          Clear filters
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #F1F5F9",
          overflow: "auto",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
        >
          <thead>
            <tr
              style={{
                background: "#F8FAFC",
                borderBottom: "1px solid #E2E8F0",
                height: 44,
              }}
            >
              {/* Checkbox */}
              <th style={{ width: 44, padding: "0 12px", textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  ref={(input) => {
                    if (input)
                      input.indeterminate =
                        someSelectedOnPage && !allSelectedOnPage;
                  }}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
              </th>

              {/* Customer */}
              <th style={{ width: 220, textAlign: "left", padding: "0 12px" }}>
                <button
                  onClick={() => onSort("name")}
                  style={{
                    background: "none", border: "none", fontSize: 11, fontWeight: 700,
                    color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  Customer
                  <span style={{ opacity: 0.6 }}>
                    {sortField === "name" ? (sortDir === 1 ? "↑" : "↓") : "↕"}
                  </span>
                </button>
              </th>

              {/* Phone */}
              <th style={{ width: 160, textAlign: "left", padding: "0 12px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Phone
                </span>
              </th>

              {/* Location */}
              <SortTh label="Location" field="location" sortField={sortField} sortDir={sortDir} onSort={onSort} width={130} />

              {/* Orders */}
              <SortTh label="Orders" field="totalOrders" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" width={80} />

              {/* Spend */}
              <SortTh label="Spend" field="totalSpend" sortField={sortField} sortDir={sortDir} onSort={onSort} align="right" width={120} />

              {/* Last Active */}
              <SortTh label="Last Active" field="lastActive" sortField={sortField} sortDir={sortDir} onSort={onSort} width={110} />

              {/* Status */}
              <SortTh label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={onSort} width={110} />

              {/* Actions */}
              <th style={{ width: 140, textAlign: "right", padding: "0 12px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Actions
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {customers.map((customer) => (
              <tr
                key={customer.id}
                onClick={() => onRowClick(customer)}
                style={{
                  height: 56,
                  borderBottom: "1px solid #F8FAFC",
                  cursor: "pointer",
                  background: selectedIds.has(customer.id) ? "#F0FDF4" : "transparent",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  if (!selectedIds.has(customer.id))
                    e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  if (!selectedIds.has(customer.id))
                    e.currentTarget.style.background = "transparent";
                }}
              >
                {/* Checkbox */}
                <td
                  style={{ padding: "0 12px", textAlign: "center" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(customer.id)}
                    onChange={(e) => onSelect(customer.id, e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                </td>

                {/* Customer — name + optional email, never breaks if email missing */}
                <td style={{ padding: "0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: avatarBg(customer.name),
                        color: avatarColor(customer.name),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials(customer.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: "#0F172A",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {customer.name}
                      </div>
                      {/* Only render email line if it exists — no empty space */}
                      {customer.email && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#94A3B8",
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {customer.email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Phone — primary identifier */}
                <td style={{ padding: "0 12px" }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#334155",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {customer.phone}
                  </span>
                </td>

                {/* Location */}
                <td
                  style={{
                    padding: "0 12px",
                    fontSize: 13,
                    color: "#64748B",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {customer.location.split(",")[0]}
                </td>

                {/* Orders */}
                <td
                  style={{
                    padding: "0 12px",
                    textAlign: "right",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#334155",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {customer.totalOrders}
                </td>

                {/* Spend */}
                <td style={{ padding: "0 12px", textAlign: "right" }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0F172A",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    KSh {customer.totalSpend.toLocaleString()}
                  </span>
                </td>

                {/* Last Active */}
                <td
                  style={{
                    padding: "0 12px",
                    fontSize: 13,
                    color: "#64748B",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {relativeTime(customer.lastActive)}
                </td>

                {/* Status */}
                <td style={{ padding: "0 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: STATUS_STYLES[customer.status].dot,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: STATUS_STYLES[customer.status].text,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                    </span>
                  </div>
                </td>

                {/* Actions dropdown */}
                <td
                  style={{ padding: "0 12px", textAlign: "right" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="cust-dropdown"
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <button
                      onClick={() =>
                        setOpenDropdownId(
                          openDropdownId === customer.id ? null : customer.id
                        )
                      }
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: "1px solid #E2E8F0",
                        background: "#fff",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="5"  r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>

                    {openDropdownId === customer.id && (
                      <div
                        style={{
                          position: "absolute",
                          right: 0,
                          top: "calc(100% + 4px)",
                          width: 180,
                          background: "#fff",
                          border: "1px solid #E2E8F0",
                          borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          zIndex: 99,
                          overflow: "hidden",
                        }}
                      >
                        <DropdownItem
                          label="View profile"
                          onClick={() => { onRowClick(customer); setOpenDropdownId(null); }}
                        />
                        <DropdownItem
                          label="Message"
                          onClick={() => { onMessageCustomer(customer.id); setOpenDropdownId(null); }}
                        />
                        <DropdownItem
                          label={customer.status === "flagged" ? "Unflag" : "Flag account"}
                          onClick={() => { onFlagCustomer(customer.id); setOpenDropdownId(null); }}
                          danger={customer.status !== "flagged"}
                        />
                        <DropdownItem
                          label={customer.status === "suspended" ? "Reactivate" : "Suspend"}
                          onClick={() => { onSuspendCustomer(customer.id); setOpenDropdownId(null); }}
                          danger={customer.status !== "suspended"}
                        />
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — exact match to ProsTable */}
      {totalPages > 1 && (
        <div
          style={{
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #F1F5F9",
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: "#64748B",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Showing {startIndex}–{endIndex} of {allCustomers.length}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                width: 32, height: 32, borderRadius: 6, border: "1px solid #E2E8F0",
                background: "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  style={{
                    width: 32, height: 32, borderRadius: 6,
                    border: currentPage === pageNum ? "none" : "1px solid #E2E8F0",
                    background: currentPage === pageNum ? "#0F172A" : "#fff",
                    color: currentPage === pageNum ? "#fff" : "#64748B",
                    cursor: "pointer", fontWeight: 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                width: 32, height: 32, borderRadius: 6, border: "1px solid #E2E8F0",
                background: "#fff", cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}